import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import { Menu, Sun, Moon } from 'lucide-react';

const pageTitles = {
  '/dashboard': 'Overview',
  '/dashboard/photos': 'Photos',
  '/dashboard/upload': 'Upload',
  '/dashboard/settings': 'Settings',
};

const DashboardLayout = ({ user, setUser }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [dark, setDark] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme');
      if (saved) return saved === 'dark';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const location = useLocation();
  const pageTitle = pageTitles[location.pathname] || 'Dashboard';

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('theme', dark ? 'dark' : 'light');
  }, [dark]);

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar
        user={user}
        setUser={setUser}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar — pill-shaped */}
        <header className="sticky top-3 z-30 mx-3 mt-3 shrink-0">
          <div className="bg-card/80 backdrop-blur-xl border border-border/50 rounded-2xl shadow-soft-card px-4 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl transition-colors cursor-pointer"
                aria-label="Toggle sidebar"
              >
                <Menu size={19} />
              </button>
              <h1 className="font-heading font-bold text-base text-foreground tracking-tight">{pageTitle}</h1>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setDark(!dark)}
                className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl transition-colors cursor-pointer"
                aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {dark ? <Sun size={17} /> : <Moon size={17} />}
              </button>

              <div className="hidden sm:flex items-center gap-2 ml-1 pl-3 border-l border-border/50">
                <div className="w-7 h-7 bg-primary/15 rounded-full flex items-center justify-center">
                  <span className="text-[10px] font-bold text-primary uppercase">{user?.username?.charAt(0) || 'U'}</span>
                </div>
                <span className="text-sm font-bold text-foreground">{user?.username}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-3 sm:p-5 lg:p-6 overflow-y-auto">
          <div className="max-w-6xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
